import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getAnimationCssVars,
  getAnimationWrapperClassName,
  normalizeComponentAnimation,
  resolveEffectiveAnimation,
} from '../utils/componentAnimation';
import { editorScrollRootRef, previewScrollRootRef } from '../utils/defaults';
import { useSectionAnimation } from './SectionAnimationContext';
import '../styles/webCreatorAnimations.css';

function getScrollRoot() {
  if (typeof document === 'undefined') return null;
  return previewScrollRootRef?.current || editorScrollRootRef?.current || window;
}

function getViewportHeight(scrollRoot) {
  if (!scrollRoot || scrollRoot === window) return window.innerHeight || 800;
  return scrollRoot.clientHeight || 800;
}

function animationSignature(config) {
  if (!config?.enabled) return 'off';
  return [
    config.preset,
    config.trigger,
    config.duration,
    config.delay,
    config.easing,
    config.distance,
    config.scale,
    config.rotate,
    config.blur,
    config.scrollThreshold,
    config.scrollOnce,
    config.scrollOffset,
    config.parallaxSpeed,
    config.parallaxDirection,
    config.usesSectionTrigger,
    config.waitForComponentId,
    config.timing,
    config.delayAfterMs,
  ].join('|');
}

export default function AnimatedComponentWrapper({
  comp,
  section,
  viewport = 'desktop',
  children,
  disabled = false,
  previewMode = false,
  previewKey = 0,
  forceActive = false,
}) {
  const sectionCtx = useSectionAnimation();
  const sectionCtxRef = useRef(sectionCtx);
  sectionCtxRef.current = sectionCtx;

  const config = useMemo(
    () => resolveEffectiveAnimation(comp, section || sectionCtx?.section, viewport),
    [comp, section, sectionCtx?.section, viewport],
  );
  const signature = animationSignature(config);

  const ref = useRef(null);
  const hasActivatedRef = useRef(false);
  const [active, setActive] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);

  const activate = () => {
    if (hasActivatedRef.current && config.scrollOnce !== false && config.trigger !== 'scrollContinuous') {
      setActive(true);
      return;
    }
    hasActivatedRef.current = true;
    setActive(true);
    sectionCtxRef.current?.registerComponentActive?.(comp.id);
  };

  // Reset only when the animation config / identity actually changes — never on sibling activations.
  useEffect(() => {
    hasActivatedRef.current = false;
    setActive(false);
    setParallaxY(0);
  }, [comp?.id, signature, previewKey, forceActive, disabled]);

  useEffect(() => {
    if (!config.enabled || disabled) {
      setActive(false);
      return undefined;
    }

    if (previewMode || forceActive) {
      if (config.trigger === 'scrollContinuous') {
        setActive(true);
        setParallaxY(config.parallaxDirection === 'down' ? 16 : -16);
        sectionCtxRef.current?.registerComponentActive?.(comp.id);
        return undefined;
      }
      // CSS fill-mode:both + delay handles the wait — activate once, no JS delay (avoids flash).
      const timer = window.setTimeout(() => {
        hasActivatedRef.current = true;
        setActive(true);
        sectionCtxRef.current?.registerComponentActive?.(comp.id);
      }, 40);
      return () => window.clearTimeout(timer);
    }

    if (config.waitForComponentId) {
      const ctx = sectionCtxRef.current;
      if (!ctx?.isComponentActive?.(config.waitForComponentId)) {
        return undefined;
      }
      // Extra wait after the reference component; CSS delay stays for the entrance itself.
      const timer = window.setTimeout(activate, Math.max(0, Number(config.delay) || 0));
      return () => window.clearTimeout(timer);
    }

    if (config.usesSectionTrigger) {
      if (!sectionCtx?.sectionActive) return undefined;
      const timer = window.setTimeout(activate, 0);
      return () => window.clearTimeout(timer);
    }

    const el = ref.current;
    if (!el) return undefined;

    if (config.trigger === 'load') {
      const timer = window.setTimeout(activate, 0);
      return () => window.clearTimeout(timer);
    }

    if (config.trigger === 'scroll' || config.trigger === 'scrollOut') {
      const scrollRoot = getScrollRoot();
      const threshold = Math.max(0, Math.min(1, config.scrollThreshold));
      const rootMarginPx = config.scrollOffset;
      let disconnected = false;

      const observer = new IntersectionObserver(
        (entries) => {
          if (disconnected) return;
          const entry = entries[0];
          if (!entry) return;
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= threshold;

          if (config.trigger === 'scroll') {
            if (isVisible) {
              activate();
              if (config.scrollOnce !== false) {
                disconnected = true;
                observer.disconnect();
              }
            } else if (!config.scrollOnce && hasActivatedRef.current) {
              setActive(false);
              hasActivatedRef.current = false;
            }
          } else if (config.trigger === 'scrollOut') {
            if (!isVisible && hasActivatedRef.current) {
              activate();
            } else if (isVisible) {
              setActive(false);
              hasActivatedRef.current = true;
            }
          }
        },
        {
          root: scrollRoot === window ? null : scrollRoot,
          threshold: [0, threshold, 0.5, 1],
          rootMargin: `${rootMarginPx}px 0px ${rootMarginPx}px 0px`,
        },
      );

      observer.observe(el);
      return () => {
        disconnected = true;
        observer.disconnect();
      };
    }

    if (config.trigger === 'scrollContinuous') {
      const scrollRoot = getScrollRoot();
      let frame = null;

      const updateParallax = () => {
        frame = null;
        const node = ref.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const vh = getViewportHeight(scrollRoot);
        const centerOffset = rect.top + rect.height / 2 - vh / 2;
        const factor = config.parallaxSpeed * 0.6;
        const delta = centerOffset * factor * (config.parallaxDirection === 'down' ? 1 : -1);
        setParallaxY(delta);
        setActive(true);
        sectionCtxRef.current?.registerComponentActive?.(comp.id);
      };

      const onScroll = () => {
        if (frame != null) return;
        frame = window.requestAnimationFrame(updateParallax);
      };

      updateParallax();
      if (scrollRoot === window) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      } else {
        scrollRoot.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      }

      return () => {
        if (frame != null) window.cancelAnimationFrame(frame);
        if (scrollRoot === window) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        } else {
          scrollRoot.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      };
    }

    return undefined;
  }, [
    config,
    signature,
    disabled,
    previewMode,
    forceActive,
    previewKey,
    comp.id,
    sectionCtx?.sectionActive,
    sectionCtx?.activationTick,
  ]);

  if (!config.enabled || disabled) {
    return children;
  }

  const className = getAnimationWrapperClassName(config, {
    active: active || forceActive,
    pending: config.trigger !== 'scrollOut' && !(active || forceActive),
  });

  // For "after component" waits, delay was already applied in JS — avoid a second CSS delay flash.
  const cssVars =
    config.waitForComponentId && active
      ? { ...getAnimationCssVars(config), '--wc-anim-delay': '0ms' }
      : getAnimationCssVars(config);

  const style = {
    ...cssVars,
    ...(config.trigger === 'scrollContinuous' && (active || forceActive)
      ? { transform: `translate3d(0, ${parallaxY}px, 0)` }
      : {}),
  };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}

export function useAnimationPreviewReplay(animation, enabled = true) {
  const [previewKey, setPreviewKey] = useState(0);
  const config = normalizeComponentAnimation(animation);

  const replay = () => setPreviewKey((k) => k + 1);

  useEffect(() => {
    if (!enabled || !config.enabled) return undefined;
    const timer = window.setTimeout(() => setPreviewKey((k) => k + 1), 120);
    return () => window.clearTimeout(timer);
  }, [
    enabled,
    config.enabled,
    config.preset,
    config.trigger,
    config.duration,
    config.delay,
    config.easing,
    config.distance,
    config.scale,
    config.rotate,
    config.blur,
    config.scrollThreshold,
    config.parallaxSpeed,
    config.parallaxDirection,
    config.timing,
    config.afterComponentId,
    config.delayAfterMs,
    config.sectionOrder,
  ]);

  return { previewKey, replay };
}
